import type { HealthVariant } from '@/lib/health-tone'
import type { EpOutcomeFilter } from '@/types/backofficeAnalytics.types'

/**
 * The delivery-lead policy: a plan ready at least this many business days
 * before the first delivery meeting counts as `onTime`. Mirrors the API's
 * `EP_LEAD_BUSINESS_DAYS` (`apps/api/src/modules/analytics/lib/escape-plan-outcomes.ts`)
 * as a literal, not an import — the two apps share no runtime. (Unlike the
 * turnaround budget, this one is a code constant on the API side too, not a
 * stored setting, so there is no payload field to read it from.)
 *
 * **Deliberately NOT unified with `forecast-queue-format.ts`'s
 * `BUDGET_WARNING_THRESHOLD_DAYS`**, even though both are 5 today — that doc
 * comment explains why on its own side, and the same reasoning applies here
 * in reverse: this is the delivery lead for a meeting, a different promise
 * on a different clock than the case-budget warning threshold.
 */
export const EP_LEAD_BUSINESS_DAYS = 5

/**
 * The shared label/tone vocabulary for Plan Delivery's four real outcomes
 * (sc-1212) — `EpOutcomesPanel`'s tiles/month bars, `KpiRecordsDialog`'s
 * per-row cell, `SpecialistPeopleTab`'s merged column, and the person detail
 * page all key off these SAME maps rather than each defining its own, so no
 * surface can disagree about what "late", "missed" or "late approval" means
 * or which colour it gets.
 *
 * `pending` is deliberately excluded — it never appears as a bar, tile tone,
 * or clickable filter, only as a caption (`formatPendingEpCaption`).
 */
export const EP_OUTCOME_LABEL: Record<EpOutcomeFilter, string> = {
  onTime: 'On time',
  late: 'Late',
  missed: 'Missed',
  lateApproval: 'Late approval',
}

/**
 * `onTime` reads healthy, `late` a warning (a process miss), `missed` an
 * error (the meeting happened with nothing ready) — the same health
 * vocabulary this page uses everywhere else.
 *
 * **`lateApproval` reads `info`, not `error` or `action`.** It is a cause,
 * not a severity (Tyler, 2026-09-29): the case was never gradeable on
 * delivery at all, because it did not exist until the client was already
 * presenting. Coloring it red or amber would read as a specialist failure —
 * exactly the false accusation this outcome exists to prevent. `info` reuses
 * `--chart-2` (see `lib/health-tone.ts`), never the health red/amber/green,
 * so it reads at a glance as "a different kind of thing," not "a worse
 * missed."
 */
export const EP_OUTCOME_TONE: Record<EpOutcomeFilter, HealthVariant> = {
  onTime: 'healthy',
  late: 'action',
  missed: 'error',
  lateApproval: 'info',
}

/**
 * `KpiRecordsDialog`'s `metricTitle` for a Plan Delivery count that was
 * clicked on a SPECIFIC person's row — `SpecialistPeopleTab`'s table/cards
 * and the person detail page (`users/$userId`) both drill into one person's
 * rows this way (sc-1212, Tyler 2026-09-30: "i cant drill down into Garrets
 * 44 late ones and investigate why"), and both need the same "whose numbers
 * are these" framing rather than inventing their own wording.
 *
 * `lateApproval` is phrased as "cases on <name>'s row", never "<name>'s late
 * approvals" — it's a PLANNER's miss reported on the specialist's row, not
 * this person's failure (see `EP_OUTCOME_TONE.lateApproval`'s doc comment
 * above), and `KpiRecordsDialog`'s own description already names the
 * approving planner.
 */
export function epOutcomePersonDrillTitle(
  personName: string,
  outcome: EpOutcomeFilter,
): string {
  if (outcome === 'lateApproval') {
    return `${personName} — cases approved too late to build`
  }
  const what =
    outcome === 'onTime'
      ? 'plans delivered on time'
      : outcome === 'late'
        ? 'plans delivered late'
        : 'plans with no plan ready'
  return `${personName} — ${what}`
}
